import React, { forwardRef } from 'react';
import styled from 'styled-components';
import ReactTypingEffect from 'react-typing-effect';
import { FaFacebookF, FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import { SiGmail } from 'react-icons/si';
import { FiGithub } from 'react-icons/fi';

import profileDark from '../assets/images/pic-no-bg-darkk.png';
import profileLight from '../assets/images/pic-no-bg.png';
import { font } from '../utils/fontSize';
import { useTheme } from '../contexts/ThemeProvider';
import { copyToClipboard } from '../utils/copyToClipboard';
import { profile } from '../data/profile';

const Hero = forwardRef(({ showNav, navItems, activeSection, onNavigate }, ref) => {
	const color = useTheme();

	return (
		<HeroContainer color={color.color} ref={ref} showNav={showNav} id="home">
			<div className="hero-container">
				<div className="header-section">
					<div className="title">
						<h2>
							Hi! I'm <span>{profile.shortName}</span>,
						</h2>
						<h1>
							<span>
								<ReactTypingEffect
									text={['AI', 'Full-Stack', 'Backend']}
									speed={5}
									eraseDelay={1000}
									eraseSpeed={50}
									typingDelay={50}
								/>
							</span>
							<br />
							Engineer
						</h1>
						<p className="current-role">
							{profile.role} <span>@</span> GoRated (ChatGenie)
						</p>
					</div>
					<ul className="social-links">
						<li>
							<a target="_blank" rel="noreferrer" href={profile.links.linkedin} aria-label="LinkedIn">
								<FaLinkedinIn />
							</a>
						</li>
						<li>
							<a target="_blank" rel="noreferrer" href={profile.links.github} aria-label="GitHub">
								<FiGithub />
							</a>
						</li>
						<li>
							<button onClick={() => copyToClipboard(profile.email, 'Gmail')} aria-label="Copy email">
								<SiGmail />
							</button>
						</li>
						<li>
							<a target="_blank" rel="noreferrer" href={profile.links.facebook} aria-label="Facebook">
								<FaFacebookF />
							</a>
						</li>
						<li>
							<a target="_blank" rel="noreferrer" href={profile.links.instagram} aria-label="Instagram">
								<FaInstagram />
							</a>
						</li>
					</ul>
					<div className="buttons">
						<a href={profile.cv} target="_blank" rel="noreferrer" className="lessImportantButton">
							Download My CV
						</a>
						<button onClick={() => onNavigate('contact')}>Hire Me!</button>
					</div>
				</div>
				<div className="profile-section">
					{color.color.isLightMode ? <img src={profileLight} alt={profile.name} /> : <img src={profileDark} alt={profile.name} />}
					<button className="projects" onClick={() => onNavigate('project')}>
						Projects
					</button>
					<button className="exp" onClick={() => onNavigate('experience')}>
						Experience
					</button>
				</div>
			</div>
			<ul className="nav-container">
				{navItems.map((item) => (
					<li key={item.id}>
						<button className={activeSection === item.id ? 'active' : ''} onClick={() => onNavigate(item.id)}>
							{item.label}
						</button>
					</li>
				))}
			</ul>
		</HeroContainer>
	);
});

export default Hero;

const HeroContainer = styled.div`
	background-color: ${({ color }) => color.background};
	color: ${({ color }) => color.text};
	display: grid;
	grid-template-columns: 1fr;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	height: 100vh;
	width: 100vw;

	.hero-container {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0 8rem;
		.header-section {
			display: flex;
			flex-direction: column;
			gap: 2rem;
			width: 100%;
			font-size: 16px;

			.title {
				text-transform: uppercase;

				h2 {
					font-size: ${font.l1};
					letter-spacing: 2.5px;
					color: ${({ color }) => color.label};

					span {
						color: ${({ color }) => color.primary};
						display: inline-block;
						font-weight: 800;
						opacity: ${({ showNav }) => (!showNav ? 1 : 0)};
						transform: ${({ showNav }) => (!showNav ? 'translateY(0)' : 'translateY(100px)')};
						transition: 0.3s ease-in-out, opacity 0.3s ease-in-out;

						&:hover,
						&:active {
							cursor: pointer;
							transform: translateY(-3px);
							filter: ${({ color }) =>
		color.isLightMode
			? 'drop-shadow(10px 5px 40px rgb(0, 0, 0))'
			: 'drop-shadow(10px 5px 40px rgb(255, 255, 255))'};
						}
					}
				}
				h1 {
					letter-spacing: 4.5px;
					span{
					text-align:center;
					letter-spacing: .5px;
					}
				}

				.current-role {
					margin-top: 1rem;
					font-size: ${font.m2};
					letter-spacing: 2.5px;
					color: ${({ color }) => color.label};
					span {
						color: ${({ color }) => color.primary};
					}
				}
			}

			.social-links {
				display: flex;
				gap: 0.5rem;
				li {
					text-decoration: none;
					list-style: none;
					a {
						display: inline-block;
						color: ${({ color }) => color.text};
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						border-radius: 15%;
						font-size: 1rem;
						padding: 0.25rem 0.5rem;
						transition: 0.1s ease-in;
						&:hover,
						&:active {
							border-radius: 30%;
							transform: translateY(-2px);
							box-shadow: 4px 2px 40px ${({ color }) => color.textShadow};
							color: ${({ color }) => color.background};
							background-color: ${({ color }) => color.primary};
						}
					}
					button {
						display: inline-block;
						color: ${({ color }) => color.text};
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						border-radius: 15%;
						font-size: 1rem;
						padding: 0.25rem 0.5rem;
						transition: 0.1s ease-in;
						&:hover,
						&:active {
							border-radius: 30%;
							transform: translateY(-2px);
							box-shadow: 4px 2px 40px ${({ color }) => color.textShadow};
							color: ${({ color }) => color.background};
							background-color: ${({ color }) => color.primary};
						}
					}
				}
			}

			.buttons {
				display: flex;
				gap: 2rem;

				button {
					color: ${({ color }) => color.background};
					font-size: 1.75rem;
					font-family: 'SHPinscher';
					text-transform: uppercase;
					letter-spacing: 2px;
					font-weight: 600;
					background-color: ${({ color }) => color.primary};
					padding: 0.5rem 1rem;
					border-radius: 3px;
					border: 2px solid ${({ color }) => color.primary};
					letter-spacing: 4.5px;
					transition: 0.1s ease-in;

					&:hover,
					&:active {
						border-radius: 12px;
						transform: translateY(-3px);
						box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						color: ${({ color }) => color.text};
						cursor: pointer;
					}
				}
				a {
					color: ${({ color }) => color.background};
					font-size: 1.75rem;
					font-family: 'SHPinscher';
					text-transform: uppercase;
					text-decoration:none;
					letter-spacing: 2px;
					font-weight: 600;
					background-color: ${({ color }) => color.primary};
					padding: 0.5rem 1rem;
					border-radius: 3px;
					border: 2px solid ${({ color }) => color.primary};
					letter-spacing: 4.5px;
					transition: 0.1s ease-in;

					&:hover,
					&:active {
						border-radius: 12px;
						transform: translateY(-3px);
						box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
						background-color: ${({ color }) => color.background};
						border: 2px solid ${({ color }) => color.primary};
						color: ${({ color }) => color.text};
						cursor: pointer;
					}
				}

				.lessImportantButton {
					background-color: ${({ color }) => color.background};
					border: 2px solid ${({ color }) => color.primary};
					color: ${({ color }) => color.text};
					&:hover,
					&:active {
						color: ${({ color }) => color.background};
						background-color: ${({ color }) => color.primary};
						box-shadow: 8px 2px 40px ${({ color }) => color.textShadow};
						cursor: pointer;
					}
				}
			}
		}

		.profile-section {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 100%;
			height: 75%;
			position: relative;
			
			img {
				max-width: 70%;
				height: auto;

				filter: ${({ color }) =>
		color.isLightMode
			? 'drop-shadow(20px 5px 30px rgba(0, 0, 0, 0.6))'
			: 'drop-shadow(20px 5px 30px rgba(255, 255, 255, 0.6))'};
			}

			button {
				position: absolute;
				font-size: 1.25rem;
				font-family: 'SHPinscher';
				text-transform: uppercase;
				letter-spacing: 2px;
				font-weight: 600;
				padding: 0.5rem 1rem;
				border-radius: 3px;
				background-color: ${({ color }) => color.background};
				border: 2px solid ${({ color }) => color.primary};
				color: ${({ color }) => color.text};
				letter-spacing: 4.5px;
				transition: 0.1s ease-in;
				&:hover,
				&:active {
					border-radius: 12px;
					transform: translateY(-3px);
					box-shadow: 8px 2px 40px ${({ color }) => color.textShadow};
					color: ${({ color }) => color.background};
					background-color: ${({ color }) => color.primary};
					border: 2px solid ${({ color }) => color.primary};
					cursor: pointer;
				}
			}

			.projects {
				position: absolute;
				top: 15vh;
				left: 4vw;
			}

			.exp {
				position: absolute;
				bottom: 15vh;
				right: 12vw;
			}
		}

		.description-section {
			text-align: end;
			width: 100%;
		}
	}

	.nav-container {
		display: flex;
		opacity: ${({ showNav }) => (!showNav ? 1 : 0)};
		transform: ${({ showNav }) => (!showNav ? 'translateY(0)' : 'translateY(100px)')};
		justify-content: center;
		align-items: center;
		gap: 3rem;
		transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
		li {
			list-style: none;
			button {
				font-family: 'SHPinscher';
				font-size: 1.5rem;
				font-weight: 800;
				text-transform: uppercase;
				cursor: pointer;
				letter-spacing: 5.5px;
				font-weight: 800;
				position: relative;
				border: none;
				background: none;
				text-decoration: none;
				text-transform: uppercase;
				transition-timing-function: cubic-bezier(0.15, 0.8, 0.15, 1);
				transition-duration: 400ms;
				transition-property: color;
				color: ${({ color }) => color.label};

				&.active {
					color: ${({ color }) => color.primary};
					&:after {
						width: 100%;
						left: 0%;
					}
				}

				&:hover {
					color: ${({ color }) => color.primary};
				}

				&:active:after,
				&:hover:after {
					width: 100%;
					left: 0%;
				}

				&:after {
					content: '';
					pointer-events: none;
					bottom: -2px;
					left: 50%;
					position: absolute;
					width: 0%;
					height: 2px;
					background-color: ${({ color }) => color.primary};
					transition-timing-function: cubic-bezier(0.25, 0.8, 0.25, 1);
					transition-duration: 400ms;
					transition-property: width, left;
				}
			}
		}
	}

	@media (max-width: 768px) {
		padding: 0 2rem;

		.hero-container {
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			gap: 5rem;
			text-align: center;
			padding: 0 1rem;

			.header-section {
				order: 2;
				align-items: center;
				justify-content: center;
				gap: 1.5rem;
				.title {
					width: 100%;
					h2 {
					}
					h1 {
						font-size: 2.5rem;
						font-weight: 800;
					}
				}

				.buttons {
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: 1rem;

					height: 50%;

				}
			}

			.profile-section {
				order: 1;
				margin-left: 10%;
				img {
					filter: ${({ color }) =>
		color.isLightMode
			? 'drop-shadow(20px 5px 30px rgba(0, 0, 0, 0.4))'
			: 'drop-shadow(20px 5px 30px rgba(255, 255, 255, 0.6))'};
				}
				.projects {
					top: 3vh;
					left: -9vw;
				}
				.exp {
					bottom: 3vh;
					right: 6vw;
				}
			}
		}

		.nav-container {
			gap: 1rem;
			display:none;
		}
	}

	@media (min-width: 769px) and (max-width: 1024px){
		.hero-container{
			padding: 0 2rem;

			.header-section {
				width: 70%;
				font-size: 20%;

				.title{
					h2{
						font-size: 1.2rem;
					}

					h1{
						font-size: 2rem;
					}
				}

				.social-links{
					gap: .5rem;
					li{
						a{
							font-size: 0.8rem;
						}
						button{
							font-size: 0.8rem

						}
					}
				}

				.buttons{
					flex-wrap: wrap;
					gap: 1rem;
					button{
						font-size: 1rem;
					}
				}
			}

			.profile-section {
				width: 70%
			}

			.profile-section {
				order: 1;
				margin-left: 10%;
				img {
					filter: ${({ color }) =>
		color.isLightMode
			? 'drop-shadow(20px 5px 30px rgba(0, 0, 0, 0.4))'
			: 'drop-shadow(20px 5px 30px rgba(255, 255, 255, 0.6))'};
				}
				.projects {
					top: 3vh;
					left: -5vw;
				}
				.exp {
					bottom: 3vh;
					right: 6vw;
				}
			}
		}
	}
`;
